import { FC } from "react";
import { useQuery } from "@tanstack/react-query";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { AppLoader } from "../../shared/components/AppLoader";
import {
  getComparisonSummary,
  type ResultsSummary,
  type ResultsSummaryPointKind
} from "../../shared/services/promptTests";

const TAG_CLASS: Record<ResultsSummaryPointKind, string> = {
  meaningful: "text-amber-700",
  cosmetic: "text-gray-400"
};

type TProps = {
  comparisonId: number;
  enabled: boolean;
};

export const ResultsChangeSummary: FC<TProps> = ({ comparisonId, enabled }) => {
  const { data, isLoading, isError } = useQuery<ResultsSummary>({
    queryKey: ["prompt-comparison-summary", comparisonId],
    queryFn: () => getComparisonSummary(comparisonId),
    enabled,
    staleTime: Infinity
  });

  if (!enabled) return null;

  const points = [...(data?.points ?? [])].sort((a, b) => {
    const rank = (kind: ResultsSummaryPointKind) => (kind === "meaningful" ? 0 : 1);
    return rank(a.kind) - rank(b.kind);
  });

  return (
    <div className="mb-6 rounded-lg border bg-card px-6 py-4">
      <FigmaTypography component="div" variant="body-2" className="text-gray-900">
        What changed in the final brief
      </FigmaTypography>

      {isLoading ? (
        <div className="mt-2 flex items-center gap-2">
          <AppLoader />
          <span className="text-[13px] text-gray-500">Comparing the final briefs…</span>
        </div>
      ) : isError || !data ? (
        <FigmaTypography component="div" variant="body-3" className="mt-2 text-red-600">
          Failed to summarize the final briefs.
        </FigmaTypography>
      ) : (
        <>
          <p className="mt-2 text-[13px] leading-[20px] text-gray-700">{data.headline}</p>
          {points.length > 0 && (
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[13px] leading-[18px]">
              {points.map((point, index) => (
                <li key={index} className={point.kind === "cosmetic" ? "text-gray-500" : "text-gray-800"}>
                  <span className={`font-medium ${TAG_CLASS[point.kind]}`}>[{point.kind}]</span> {point.text}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
